import { CheckCircledIcon, PlusIcon } from "@radix-ui/react-icons";
import { PopoverAnchor } from "@radix-ui/react-popover";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { useState } from "react";
import { Button as ButtonPrimitive } from "react-aria-components";

import { preventBubble } from "@/components/canvas-editor/utils";
import { ColorPreview } from "@/components/ColorPreview";
import { CreateTagDialog } from "@/components/CreateTagDialog";
import { useToast } from "@/components/hooks/use-toast";
import { Spinner } from "@/components/Spinner";
import { TagListItem } from "@/components/TagListItem";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SimpleTooltip } from "@/components/ui/tooltip";
import {
  createTaggedItem,
  CreateTaggedItemOptions,
  deleteTaggedItem,
  DeleteTaggedItemOptions,
  listTagsQueryOptions,
} from "@/lib/api";
import { Feed } from "@/lib/types";
import { cn } from "@/lib/utils";

export function AddTagPopover({
  children,
  data,
  type,
  open = false,
  onOpenChange,
  isAnchor = false,
}: {
  children?: React.ReactNode;
  data: Pick<Feed, "id" | "tags">;
  type: "feed" | "custom-node";
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  isAnchor?: boolean;
}) {
  const { data: tags, isPending } = useQuery(listTagsQueryOptions());
  const { toast } = useToast();
  const [inputValue, setInputValue] = useState("");
  const [createTagDialogOpen, setCreateTagDialogOpen] = useState(false);

  const createTaggedItemMutation = useMutation({
    mutationFn: async (options: CreateTaggedItemOptions) => {
      await createTaggedItem(options);
    },
    onError: (error: AxiosError<{ detail: string }>) => {
      toast({
        title: "Error",
        description: error.response?.data?.detail || error.message,
      });
    },
  });

  const removeTaggedItemMutation = useMutation({
    mutationFn: async (options: DeleteTaggedItemOptions) => {
      await deleteTaggedItem(options);
    },
    onError: (error: AxiosError<{ detail: string }>) => {
      toast({
        title: "Error",
        description: error.response?.data?.detail || error.message,
      });
    },
  });

  const Wrapper = isAnchor ? PopoverAnchor : PopoverTrigger;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {children && (
        <SimpleTooltip message="Create a tag">
          <Wrapper asChild>{children}</Wrapper>
        </SimpleTooltip>
      )}
      <PopoverContent className="p-0" {...preventBubble}>
        <Command>
          <CommandInput
            placeholder="Search tags"
            value={inputValue}
            onValueChange={setInputValue}
          />
          <CommandList>
            {isPending ? (
              <div className="h-10 w-full flex items-center justify-center text-slate-dim">
                <Spinner />
              </div>
            ) : (
              <CommandGroup>
                {tags?.map((tag) => {
                  const hasTag = data.tags.some((t) => t.id === tag.id);

                  return (
                    <CommandItem
                      key={`${tag.id}`}
                      value={`${tag.display_name}`}
                      onSelect={() => {
                        if (hasTag) {
                          removeTaggedItemMutation.mutate({
                            tag_id: tag.id,
                            item_id: data.id,
                            item_type:
                              type === "feed" ? "algorithm" : "component",
                          });
                        } else {
                          createTaggedItemMutation.mutate({
                            tag_id: tag.id,
                            item_id: data.id,
                            item_type:
                              type === "feed" ? "algorithm" : "component",
                          });
                        }
                      }}
                    >
                      {(tag.id === createTaggedItemMutation.variables?.tag_id &&
                        createTaggedItemMutation.isPending) ||
                      (tag.id === removeTaggedItemMutation.variables?.tag_id &&
                        removeTaggedItemMutation.isPending) ? (
                        <Spinner />
                      ) : hasTag ? (
                        <CheckCircledIcon />
                      ) : (
                        <ColorPreview color={tag.color} />
                      )}
                      {tag.display_name}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {inputValue && (
              <CreateTagDialog
                open={createTagDialogOpen}
                onOpenChange={setCreateTagDialogOpen}
                defaultName={inputValue}
                key={inputValue}
              >
                <CommandItem
                  value={inputValue}
                  onSelect={() => {
                    setCreateTagDialogOpen(true);
                  }}
                >
                  <PlusIcon />
                  {`Create: ${inputValue}`}
                </CommandItem>
              </CreateTagDialog>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function TagListItems({
  data,
  to,
}: {
  data: Pick<Feed, "id" | "tags">;
  to: "/app" | "/custom-nodes";
}) {
  const location = useLocation();
  const navigate = useNavigate();

  if (data.tags.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-1">
      {data.tags
        .sort((a, b) => a.display_name.localeCompare(b.display_name))
        .map((tag) => (
          <ButtonPrimitive
            key={tag.id}
            onPress={() => {
              navigate({
                to: to,
                replace: true,
                search: {
                  ...location.search,
                  tags: Array.from(
                    new Set([...(location.search.tags || []), tag.id]),
                  ),
                },
              }).catch(console.error);
            }}
          >
            <TagListItem tag={tag} />
          </ButtonPrimitive>
        ))}
    </div>
  );
}

export function TagList({
  data,
  type,
  size = "smIcon",
  showOnHover = true,
}: {
  data: Pick<Feed, "id" | "tags">;
  type: "feed" | "custom-node";
  size?: "smIcon" | "xsIcon";
  showOnHover?: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex items-center gap-1 group" {...preventBubble}>
      <TagListItems
        data={data}
        to={type === "custom-node" ? "/custom-nodes" : "/app"}
      />
      <AddTagPopover data={data} type={type} open={open} onOpenChange={setOpen}>
        <Button
          size={size}
          variant="outline"
          className={cn(
            showOnHover &&
              "hidden md:flex opacity-0 group-hover:opacity-100 aria-expanded:opacity-100 transition-opacity",
          )}
        >
          <span className="sr-only">Add tag</span>
          <PlusIcon />
        </Button>
      </AddTagPopover>
    </div>
  );
}
